import { BaseManager } from '../core/base-manager';
import { ManagerRegistry } from '../core/manager-registry';
import { MeasureDockPanel } from '../components/measure-dock-panel';
import { themeManager } from '../services/theme';
import { localeManager } from '../services/locale';
import { getModeBycallBackType, type CallBackType } from '../types/measure';

interface EngineMeasureData {
    type: CallBackType;
}

export class MeasureDockManager extends BaseManager {
    private panel: MeasureDockPanel | null = null;
    private unsubscribeTheme: (() => void) | null = null;
    private unsubscribeLocale: (() => void) | null = null;
    private unsubscribeDockState: (() => void) | null = null;
    private unsubscribeMeasureEvents: (() => void) | null = null;

    constructor(registry: ManagerRegistry) {
        super(registry);
    }

    public init(): void {
        if (!this.unsubscribeTheme) {
            this.unsubscribeTheme = themeManager.subscribe(() => {
                this.applyPresentation();
            });
        }

        if (!this.unsubscribeLocale) {
            this.unsubscribeLocale = localeManager.subscribe(() => {
                this.applyPresentation();
            });
        }

        if (!this.unsubscribeDockState) {
            this.unsubscribeDockState = this.registry.bottomDock?.onStateChange((state) => {
                if (state.id !== 'measure' || state.open) {
                    if (state.id === 'measure' && state.open) {
                        this.ensureMeasureEventSubscription();
                    }
                    return;
                }
                this.engineComponent?.deactivateMeasure();
            }) ?? null;
        }
        this.ensureMeasureEventSubscription();
    }

    public destroy(): void {
        if (this.unsubscribeTheme) {
            this.unsubscribeTheme();
            this.unsubscribeTheme = null;
        }
        if (this.unsubscribeLocale) {
            this.unsubscribeLocale();
            this.unsubscribeLocale = null;
        }
        if (this.unsubscribeDockState) {
            this.unsubscribeDockState();
            this.unsubscribeDockState = null;
        }
        if (this.unsubscribeMeasureEvents) {
            this.unsubscribeMeasureEvents();
            this.unsubscribeMeasureEvents = null;
        }

        this.panel?.destroy();
        this.panel = null;
        super.destroy();
    }

    public getPanelElement(): HTMLElement {
        this.ensureMeasureEventSubscription();
        if (!this.panel) {
            this.panel = new MeasureDockPanel({
                defaultMode: 'distance',
                defaultExpanded: false,
                defaultClearHeightDirection: 1,
                defaultClearHeightSelectType: 'point',
                onModeChange: (mode) => {
                    this.engineComponent?.activateMeasure(mode);
                },
                onClearAll: () => {
                    this.engineComponent?.clearAllMeasures();
                },
                onConfigSave: (config) => {
                    this.engineComponent?.saveMeasureSetting({
                        unit: config.unit,
                        precision: config.precision
                    });
                },
                onClearHeightDirectionChange: (direction) => {
                    this.engineComponent?.setClearHeightDirection(direction);
                },
                onClearHeightSelectTypeChange: (selectType) => {
                    this.engineComponent?.setClearHeightSelectType(selectType);
                }
            });
            this.panel.init();
            this.panel.switchMode('distance');
            this.engineComponent?.activateMeasure('distance');
            this.engineComponent?.setClearHeightDirection(1);
            this.engineComponent?.setClearHeightSelectType('point');
            const config = this.panel.getConfig();
            this.engineComponent?.saveMeasureSetting({
                unit: config.unit,
                precision: config.precision
            });
        } else {
            this.panel.switchMode('distance');
            this.engineComponent?.activateMeasure('distance');
        }
        this.applyPresentation();
        return this.panel.element;
    }

    private ensureMeasureEventSubscription(): void {
        if (this.unsubscribeMeasureEvents) {
            return;
        }

        const ec = this.engineComponent;
        if (!ec) {
            console.warn('[MeasureDockManager] skip callback binding: engine component not ready yet');
            return;
        }

        const changedHandler = (data: EngineMeasureData) => {
            this.handleMeasureCallback('measure-changed', data);
        };
        const clickHandler = (data: EngineMeasureData) => {
            this.handleMeasureCallback('measure-click', data);
        };
        const quitHandler = () => {
            console.log('[MeasureDockManager] quit_measure_draw received');
            this.panel?.clearActiveMode();
            this.engineComponent?.resetMeasureState();
        };

        ec.onRawEvent('measure-changed', changedHandler);
        ec.onRawEvent('measure-click', clickHandler);
        ec.onRawEvent('quit_measure_draw', quitHandler);
        this.unsubscribeMeasureEvents = () => {
            ec.offRawEvent('measure-changed', changedHandler);
            ec.offRawEvent('measure-click', clickHandler);
            ec.offRawEvent('quit_measure_draw', quitHandler);
        };

        console.log('[MeasureDockManager] raw event callbacks bound');
    }

    private applyPresentation(): void {
        if (!this.panel) {
            return;
        }
        this.panel.setTheme(themeManager.getTheme());
        this.panel.setLocales();
    }

    private handleMeasureCallback(eventName: 'measure-changed' | 'measure-click', data: EngineMeasureData): void {
        const isOpen = this.registry.bottomDock?.isOpen('measure') ?? false;
        const mode = getModeBycallBackType(data.type);

        console.log('[MeasureDockManager] callback received', {
            event: eventName,
            type: data.type,
            mode,
            dockOpen: isOpen,
            hasPanel: Boolean(this.panel)
        });

        if (!isOpen) {
            console.log('[MeasureDockManager] skip mode switch: measure dock is closed');
            return;
        }

        if (!mode || !this.panel) {
            console.log('[MeasureDockManager] skip mode switch: invalid mode or panel not ready');
            return;
        }

        this.panel.switchMode(mode, false);
        this.engineComponent?.activateMeasure(mode);
        console.log('[MeasureDockManager] switched mode by callback', { event: eventName, mode });
    }
}
